import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { getQueryTimeoutMs } from '#pplx/frontend/api-clients/core/utils.js';
import { getNativeOAuthConnectorRedirectUrl, isConnectorIdSupportingRedirect as isNativeOAuthConnectorId, } from '#pplx/frontend/apps/ask/connectors/auth/nativeOAuthConnectors.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { connectionsClient } from '#pplx_generated/typespec/rest/connections/client.js';
import { connectorsClient } from '#pplx_generated/typespec/rest/connectors/client.js';
export const isConnectorSupportingRedirect = (connector) => {
    return isConnectorIdSupportingRedirect(connector.name);
};
export const isConnectorIdSupportingRedirect = (connectorId) => isNativeOAuthConnectorId(connectorId);
export const getConnectorRedirectURL = async ({ name, referrer, referrerId, redirectPath, redirectOrigin, unauthedRedirectPath, autoClose, forceAccountSelection, reason, }) => {
    try {
        return await getNativeOAuthConnectorRedirectUrl({
            name,
            referrer,
            referrerId,
            redirectPath,
            redirectOrigin,
            unauthedRedirectPath,
            autoClose,
            forceAccountSelection,
            reason,
        });
    }
    catch (error) {
        logger.error('Error fetching factset OAuth redirect URL:', error);
        return null;
    }
};
export const disconnectConnector = async ({ connectorName, reason, connectionUUID, }) => {
    const { data, error, response } = await connectorsClient.GET(`/rest/connectors/{connector_id}/disconnect`, reason, {
        timeoutMs: getQueryTimeoutMs({ productionMs: 500 }),
        numRetries: 1,
        reason,
        params: {
            path: {
                connector_id: connectorName,
            },
            query: {
                connection_uuid: connectionUUID,
            },
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to disconnect connector',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
export const disconnectConnection = async ({ connectionUUID, reason, }) => {
    const { data, error, response } = await connectionsClient.POST(`/rest/connections/{uuid}/update`, reason, {
        params: {
            path: {
                uuid: connectionUUID,
            },
        },
        body: { disconnect: true },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to disconnect connection',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
export const setConnectionDefault = async ({ connectionUUID, reason, }) => {
    const { data, error, response } = await connectionsClient.POST(`/rest/connections/{uuid}/set_default`, reason, {
        params: {
            path: {
                uuid: connectionUUID,
            },
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to set connection as default',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
export const setConnectionNickname = async ({ connectionUUID, nickname, reason, }) => {
    const { data, error, response } = await connectionsClient.POST(`/rest/connections/{uuid}/nickname`, reason, {
        params: {
            path: {
                uuid: connectionUUID,
            },
        },
        body: { nickname },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to set connection nickname',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
export const disconnectFileConnectorAndDeleteFiles = async ({ connectionUUID, reason, }) => {
    const { data, error, response } = await connectionsClient.POST(`/rest/connections/{uuid}/delete`, reason, {
        params: {
            path: {
                uuid: connectionUUID,
            },
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to disconnect file connector and delete files',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
const uploadSharepointFiles = async ({ selections, fileRepoInfo, connectionUuid, reason, }) => {
    const { error, data, response } = await connectorsClient.POST('/rest/connectors/sharepoint/files', reason, {
        body: {
            file_ids: [],
            drive_ids: [],
            site_id: null,
            selections,
            file_repository_info: fileRepoInfo,
            connection_uuid: connectionUuid,
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
const uploadBoxFiles = async ({ selections, fileRepoInfo, connectionUuid, reason, }) => {
    const { error, data, response } = await connectorsClient.POST('/rest/connectors/box/files', reason, {
        body: {
            file_ids: [],
            folder_ids: [],
            selections,
            file_repository_info: fileRepoInfo,
            connection_uuid: connectionUuid,
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
const uploadDriveFiles = async ({ connectorName, selections, fileRepoInfo, connectionUuid, reason, }) => {
    const { error, data, response } = await connectorsClient.POST(`/rest/connectors/${connectorName}/files`, reason, {
        body: {
            file_ids: [],
            drive_ids: [],
            selections,
            file_repository_info: fileRepoInfo,
            connection_uuid: connectionUuid,
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
export const uploadConnectorFiles = async ({ connectorName, selections, fileRepoInfo, connectionUuid, reason, }) => {
    if (connectorName === 'sharepoint') {
        return uploadSharepointFiles({
            selections: selections,
            fileRepoInfo,
            connectionUuid,
            reason,
        });
    }
    else if (connectorName === 'box') {
        return uploadBoxFiles({
            selections: selections,
            fileRepoInfo,
            connectionUuid,
            reason,
        });
    }
    else {
        return uploadDriveFiles({
            connectorName,
            selections: selections,
            fileRepoInfo,
            connectionUuid,
            reason,
        });
    }
};
export const refreshBoxPickerCredentials = async ({ connectionUuid, reason, }) => {
    const { data, error, response } = await connectorsClient.POST('/rest/connectors/box/picker', reason, {
        params: { query: { connection_uuid: connectionUuid ?? undefined } },
    });
    if (error || !data) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: 'Failed to refresh box picker credentials',
            cause: error,
            status: response.status ?? 0,
        });
    }
    return data;
};
//# sourceMappingURL=connectorMutations.js.map